import type { TFunction } from "i18next";

import type {
  MarketplaceFulfillmentStatus,
  OrderScriptRow,
} from "../types/marketplace.types";

type HookLineDelivery = Pick<
  OrderScriptRow,
  "hookDeliveries" | "preflightUploadId" | "preflightUrl" | "scriptReviewId"
>;

type HookLine = HookLineDelivery & Pick<OrderScriptRow, "fulfillmentStatus">;

export interface OrderLineHookVideo {
  /** Null for the primary, which is addressed without a hookKey. */
  hookKey: string | null;
  index: number;
  isPrimary: boolean;
  hasUpload: boolean;
  scriptReviewId: string | null;
  preflightUrl: string | null;
}

export interface OrderLineHookProgress {
  delivered: number;
  total: number;
}

export type OrderLineHookVideosState = "hidden" | "in-production" | "videos";

/** A multi-hook line keeps these statuses until every hook has a video. */
const AWAITING_HOOK_VIDEO_STATUSES: readonly MarketplaceFulfillmentStatus[] = [
  "SCRIPT",
  "SENT_TO_PRODUCTION",
];

export function isMultiHookLine(row: Pick<OrderScriptRow, "hookDeliveries">): boolean {
  return (row.hookDeliveries?.length ?? 0) > 0;
}

export function isOrderLineAwaitingHookVideos(
  status: MarketplaceFulfillmentStatus,
): boolean {
  return AWAITING_HOOK_VIDEO_STATUSES.includes(status);
}

/** Primary first from the line's columns, then alternates by position. Empty on one-hook lines. */
export function listOrderLineHookVideos(row: HookLineDelivery): OrderLineHookVideo[] {
  if (!isMultiHookLine(row)) return [];
  const alternates = [...(row.hookDeliveries ?? [])].sort(
    (left, right) => left.position - right.position,
  );
  return [
    {
      hookKey: null,
      index: 1,
      isPrimary: true,
      hasUpload: Boolean(row.preflightUploadId),
      scriptReviewId: row.scriptReviewId,
      preflightUrl: row.preflightUrl,
    },
    ...alternates.map((delivery, offset) => ({
      hookKey: delivery.hookKey,
      index: offset + 2,
      isPrimary: false,
      hasUpload: Boolean(delivery.preflightUploadId),
      scriptReviewId: delivery.scriptReviewId,
      preflightUrl: null,
    })),
  ];
}

export function describeOrderLineHookProgress(row: HookLine): OrderLineHookProgress | null {
  if (!isOrderLineAwaitingHookVideos(row.fulfillmentStatus)) return null;
  const videos = listOrderLineHookVideos(row);
  if (videos.length === 0) return null;
  return {
    delivered: videos.filter((video) => video.hasUpload).length,
    total: videos.length,
  };
}

export function describeOrderLineHookVideos(row: HookLine): {
  state: OrderLineHookVideosState;
  videos: OrderLineHookVideo[];
} {
  const videos = listOrderLineHookVideos(row);
  if (videos.length === 0 || row.fulfillmentStatus === "CANCELLED") {
    return { state: "hidden", videos };
  }
  if (isOrderLineAwaitingHookVideos(row.fulfillmentStatus)) {
    return { state: "in-production", videos };
  }
  return { state: "videos", videos };
}

export function orderLineHookLabel(
  t: TFunction,
  hook: { index: number; isPrimary: boolean },
): string {
  return hook.isPrimary
    ? t("marketplace.orderHooks.primaryLabel", {
        index: hook.index,
        defaultValue: "Hook {{index}} (primary)",
      })
    : t("marketplace.orderHooks.label", {
        index: hook.index,
        defaultValue: "Hook {{index}}",
      });
}

/**
 * The line's single review link opens the primary's video. On a multi-hook line it stays
 * hidden until the whole set is submitted, so clients never see a partial delivery.
 */
export function canShowOrderLineReview(row: HookLine): boolean {
  return !isMultiHookLine(row) || !isOrderLineAwaitingHookVideos(row.fulfillmentStatus);
}
